import type { AudioBackend, AudioAsset, PlaybackHandle } from './audio-controller'

export const supportsAutoplayBlocking = true

// Shared AudioContext to avoid browser limits (typically 6-8 contexts max)
let sharedAudioContext: AudioContext | null = null

function getSharedAudioContext(): AudioContext {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new (
      window.AudioContext ||
      // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-member-access -- webkit fallback; browser API
      (window as any).webkitAudioContext
    )()
  }
  return sharedAudioContext
}

class BrowserAudioBackend implements AudioBackend {
  private audioContext: AudioContext | null = null
  private gainNode: GainNode | null = null
  private currentSource: AudioBufferSourceNode | null = null
  private ownsContext = false

  private ensureContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = getSharedAudioContext()
      this.ownsContext = false
      this.gainNode = this.audioContext.createGain()
      this.gainNode.connect(this.audioContext.destination)
    }

    return this.audioContext
  }

  private ensureGainNode(ctx: AudioContext): GainNode {
    if (!this.gainNode) {
      this.gainNode = ctx.createGain()
      this.gainNode.connect(ctx.destination)
    }

    return this.gainNode
  }

  async load(url: string, signal: AbortSignal): Promise<AudioAsset> {
    const ctx = this.ensureContext()
    const response = await fetch(url, { signal })

    if (!response.ok) {
      throw new Error(`Failed to load audio (${response.status})`)
    }

    const arrayBuffer = await response.arrayBuffer()
    const buffer = await ctx.decodeAudioData(arrayBuffer)

    return {
      url,
      buffer,
      durationMs: buffer.duration * 1000,
    }
  }

  async decode(buffer: ArrayBuffer): Promise<AudioAsset> {
    const ctx = this.ensureContext()
    const decoded = await ctx.decodeAudioData(buffer.slice(0))

    return {
      url: '',
      buffer: decoded,
      durationMs: decoded.duration * 1000,
    }
  }

  async play(
    asset: AudioAsset,
    offsetSeconds: number,
    onEnded: () => void,
  ): Promise<PlaybackHandle> {
    const ctx = this.ensureContext()

    if (ctx.state === 'suspended') {
      await ctx.resume()
    }

    this.stop()

    const gain = this.ensureGainNode(ctx)
    const source = ctx.createBufferSource()
    if (!asset.buffer) throw new Error('Missing decoded audio buffer')
    source.buffer = asset.buffer
    source.connect(gain)
    // oxlint-disable-next-line unicorn/prefer-add-event-listener -- AudioBufferSourceNode.onended is the canonical API; addEventListener('ended') works but onended is more common in Web Audio
    source.onended = () => {
      if (this.currentSource === source) {
        this.currentSource = null
      }
      onEnded()
    }

    source.start(0, offsetSeconds)
    this.currentSource = source

    return {
      stop: () => {
        if (this.currentSource === source) {
          // oxlint-disable-next-line unicorn/prefer-add-event-listener -- AudioBufferSourceNode.onended is the canonical API; addEventListener('ended') works but onended is more common in Web Audio
          source.onended = null
          try {
            source.stop()
          } catch {
            // Source may already be stopped
          }
          try {
            source.disconnect()
          } catch {
            // Already disconnected
          }
          this.currentSource = null
        }
      },
      startedAtMs: ctx.currentTime * 1000,
    }
  }

  stop(): void {
    if (!this.currentSource) {
      return
    }

    const source = this.currentSource
    this.currentSource = null
    // oxlint-disable-next-line unicorn/prefer-add-event-listener -- AudioBufferSourceNode.onended is the canonical API; addEventListener('ended') works but onended is more common in Web Audio
    source.onended = null

    try {
      source.stop()
    } catch {
      // Source may already be stopped
    }

    try {
      source.disconnect()
    } catch {
      // Source may already be disconnected
    }
  }

  getCurrentTimeMs(): number {
    if (!this.audioContext) {
      return 0
    }

    return this.audioContext.currentTime * 1000
  }

  setVolume(volume: number): void {
    if (!this.audioContext) {
      this.ensureContext()
    }

    if (!this.audioContext || this.audioContext.state === 'closed') {
      return
    }

    const gain = this.ensureGainNode(this.audioContext)
    gain.gain.setValueAtTime(volume, this.audioContext.currentTime)
  }

  close(): void {
    this.stop()
    // Only close ctx if we own it (not shared)
    if (
      this.ownsContext &&
      this.audioContext &&
      this.audioContext.state !== 'closed'
    ) {
      void this.audioContext.close()
    }

    // Disconnect gain node from shared ctx
    if (this.gainNode) {
      try {
        this.gainNode.disconnect()
      } catch {
        // Already disconnected
      }
    }

    this.audioContext = null
    this.gainNode = null
  }
}

export function defaultCreateBackend(): AudioBackend {
  return new BrowserAudioBackend()
}

